import React, { useState, useEffect } from "react";
import { Select, Input, Form } from "antd";
import Layout from "../layout/Layout.js";
import toast from "react-hot-toast";
import stateDistricts from '../../../assets/data/stateDistricts.jsx';
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import { AddStudentApi } from "../../../api/request/Student.js";
import { Helmet } from 'react-helmet';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { 
    FaMapMarkerAlt, FaPhone, FaEnvelope, FaClock, 
    FaBuilding, FaCheckCircle, FaArrowRight
} from 'react-icons/fa';

const { TextArea } = Input;
const { Option } = Select;

const Contact = () => {
    const [form] = Form.useForm();
    const [initialValues, setInitialValues] = useState({ 
        First_Name: '', Last_Name: '', Contact_Number: '', 
        State: '', District: '', Secondary_Contact: '', Address: '' 
    });
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const [isFormChanged, setIsFormChanged] = useState(false);
    const [selectedState, setSelectedState] = useState('');
    const [districtOptions, setDistrictOptions] = useState([]);

    useEffect(() => {
        AOS.init({ 
            duration: 1000, 
            once: true,
            offset: 100
        });
    }, []);

    const handleFieldsChange = (_, allFields) => {
        const isChanged = allFields.some(field => field.touched);
        setIsFormChanged(isChanged);
    };

    const filterOption = (input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    const handleStateChange = (value) => {
        setSelectedState(value);
        setDistrictOptions(stateDistricts[value] || []);
    };

    const handleSubmit = async (values) => {
        setIsSubmitLoading(true);
        let params = { ...values };
        try {
            const { data } = await AddStudentApi(params);
            if (data?.success) {
                toast.success(data?.message || 'Thank you! We will contact you soon.');
                form.resetFields();
                setInitialValues({ 
                    First_Name: '', Last_Name: '', Contact_Number: '', 
                    State: '', District: '', Secondary_Contact: '', Address: '' 
                });
            }
        } catch (error) {
            toast.error(error?.response?.data?.error?.message || 'An error occurred. Please try again.');
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const offices = [
        {
            title: "Head Office - Muzaffarpur",
            icon: <FaBuilding className="text-3xl" />,
            address: "NH-28 shiv mandir Near Khabra Resonance international school",
            city: "Muzaffarpur, Bihar, 842002",
            country: "India",
            phone: ["7654108215", "9934001660"],
            email: "admin@educationaleternity.com"
        },
        {
            title: "Branch Office - Bengaluru",
            icon: <FaBuilding className="text-3xl" />,
            address: "5th floor, Trifecta Starlight Trifecta Adatto, 21, ITPL Main Rd, Garudachar Palya, Mahadevapura",
            city: "Bengaluru, Karnataka, 560048",
            country: "India",
            phone: ["9934001660", "7654108215"],
            email: "admin@educationaleternity.com"
        }
    ];

    return (
        <Layout>
            <Helmet>
                <title>Contact Us - Educational Eternity | Get in Touch</title>
                <meta name="description" content="Contact Educational Eternity for inquiries, support, or to schedule a demo. We're here to help transform your educational institution with our AI-powered ERP system." />
                <meta name="keywords" content="Contact Educational Eternity, Education ERP Support, School Management Software Contact, Educational Technology Support" />
            </Helmet>

            {/* Hero Section */}
            <section className="relative w-full h-[50vh] bg-cover bg-center flex items-center justify-center overflow-hidden"
                style={{ backgroundImage: `url(assets/images/123.gif)` }}>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-900/80 to-indigo-900/80"></div>
                <div className="relative z-10 text-center px-4">
                    <h1 data-aos="fade-up" className="font-bold text-5xl md:text-7xl text-white mb-4">
                        Contact Us
                    </h1>
                    <p data-aos="fade-up" data-aos-delay="100" className="text-xl md:text-2xl text-blue-100 max-w-3xl mx-auto">
                        We're here to help transform your educational institution
                    </p>
                </div>
            </section>

            {/* Contact Form Section */}
            <section className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-12" data-aos="fade-up">
                        <span className="inline-block px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-semibold mb-4">
                            Schedule a Demo
                        </span>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Get in Touch
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Fill out the form below and our team will get back to you within 24 hours. We're excited to help you transform your educational institution!
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        <div className="bg-white rounded-2xl shadow-2xl p-8 md:p-12" data-aos="fade-up">
                            <Form 
                                className='w-full' 
                                autoComplete="off" 
                                form={form} 
                                initialValues={initialValues} 
                                onFinish={handleSubmit} 
                                onFieldsChange={handleFieldsChange}
                            >
                                <div className="grid sm:grid-cols-2 gap-6 mb-6">
                                    <div>
                                        <label className="text-base text-gray-700 font-semibold mb-2 block">
                                            First Name <span className="text-red-500">*</span>
                                        </label>
                                        <Form.Item 
                                            name="First_Name" 
                                            rules={[{ required: true, message: 'Please enter First Name.' }]}
                                        >
                                            <Input 
                                                placeholder="Enter your first name" 
                                                className="w-full ps-4 py-3 rounded-lg border-gray-300" 
                                                size="large"
                                            />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <label className="text-base text-gray-700 font-semibold mb-2 block">
                                            Last Name <span className="text-red-500">*</span>
                                        </label>
                                        <Form.Item 
                                            name="Last_Name" 
                                            rules={[{ required: true, message: 'Please enter Last Name.' }]}
                                        >
                                            <Input 
                                                placeholder="Enter your last name" 
                                                className="w-full ps-4 py-3 rounded-lg border-gray-300" 
                                                size="large"
                                            />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <label className="text-base text-gray-700 font-semibold mb-2 block">
                                            Phone Number <span className="text-red-500">*</span>
                                        </label>
                                        <Form.Item 
                                            name="Contact_Number" 
                                            rules={[{ required: true, message: 'Please enter phone number.' }]}
                                        >
                                            <Input 
                                                type="tel" 
                                                placeholder="Enter your phone number" 
                                                className="w-full ps-4 py-3 rounded-lg border-gray-300" 
                                                size="large"
                                            />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <label className="text-base text-gray-700 font-semibold mb-2 block">
                                            Alternative Phone (Optional)
                                        </label>
                                        <Form.Item name="Secondary_Contact">
                                            <Input 
                                                type="tel" 
                                                placeholder="Alternative phone number" 
                                                className="w-full ps-4 py-3 rounded-lg border-gray-300" 
                                                size="large"
                                            />
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <label className="text-base text-gray-700 font-semibold mb-2 block">
                                            State <span className="text-red-500">*</span>
                                        </label>
                                        <Form.Item 
                                            name="State" 
                                            rules={[{ required: true, message: "Please select a state." }]}
                                        >
                                            <Select
                                                showSearch
                                                placeholder="Select your state"
                                                className="w-full"
                                                size="large"
                                                filterOption={filterOption}
                                                onChange={handleStateChange}
                                            >
                                                {Object.keys(stateDistricts).map((state) => (
                                                    <Option key={state} value={state}>
                                                        {state}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </div>
                                    <div>
                                        <label className="text-base text-gray-700 font-semibold mb-2 block">
                                            District <span className="text-red-500">*</span>
                                        </label>
                                        <Form.Item 
                                            name="District" 
                                            rules={[{ required: true, message: "Please select a district." }]}
                                        >
                                            <Select
                                                showSearch
                                                placeholder="Select your district"
                                                className="w-full"
                                                size="large"
                                                filterOption={filterOption}
                                                disabled={!selectedState}
                                            >
                                                {districtOptions.map((district) => (
                                                    <Option key={district} value={district}>
                                                        {district}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    </div>
                                </div>
                                <div className="mb-6">
                                    <label className="text-base text-gray-700 font-semibold mb-2 block">
                                        Address <span className="text-red-500">*</span>
                                    </label>
                                    <Form.Item 
                                        name="Address" 
                                        rules={[{ required: true, message: 'Please enter address.' }]}
                                    >
                                        <TextArea 
                                            className="w-full ps-4 py-3 rounded-lg border-gray-300" 
                                            placeholder="Enter your complete address"
                                            autoSize={{ minRows: 4, maxRows: 6 }} 
                                        />
                                    </Form.Item>
                                </div>
                                <div className="flex justify-center">
                                    <LoadableButton 
                                        type='submit' 
                                        className='bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold px-12 py-4 rounded-full hover:shadow-xl transform hover:scale-105 transition-all duration-300 flex items-center gap-2' 
                                        lable='Submit Request' 
                                        disabled={!isFormChanged || isSubmitLoading} 
                                        isLoading={isSubmitLoading} 
                                        loadingLable='Submitting...' 
                                    />
                                </div>
                            </Form>
                        </div>
                    </div>
                </div>
            </section>

            {/* Contact Information Section */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16" data-aos="fade-up">
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Our Offices
                        </h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            Visit us at our offices or reach out through any of the contact methods below. We're here to assist you!
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                        {offices.map((office, idx) => (
                            <div
                                key={idx}
                                data-aos="fade-up"
                                data-aos-delay={idx * 100}
                                className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
                            >
                                <div className="flex items-center mb-6">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 rounded-xl mr-4">
                                        {office.icon}
                                    </div>
                                    <h3 className="text-2xl font-bold text-gray-900">{office.title}</h3>
                                </div>
                                
                                <div className="space-y-4">
                                    <div className="flex items-start">
                                        <FaMapMarkerAlt className="text-blue-600 text-xl mr-4 mt-1 flex-shrink-0" />
                                        <div>
                                            <p className="text-gray-700 font-medium">{office.address}</p>
                                            <p className="text-gray-600">{office.city}</p>
                                            <p className="text-gray-600">{office.country}</p>
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center">
                                        <FaPhone className="text-blue-600 text-xl mr-4 flex-shrink-0" />
                                        <div>
                                            {office.phone.map((ph, i) => (
                                                <a 
                                                    key={i}
                                                    href={`tel:${ph}`}
                                                    className="text-gray-700 hover:text-blue-600 font-medium block"
                                                >
                                                    {ph}
                                                </a>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    <div className="flex items-center">
                                        <FaEnvelope className="text-blue-600 text-xl mr-4 flex-shrink-0" />
                                        <a 
                                            href={`mailto:${office.email}`}
                                            className="text-gray-700 hover:text-blue-600 font-medium"
                                        >
                                            {office.email}
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quick Contact Section */}
            <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-700">
                <div className="container mx-auto px-4 text-center" data-aos="fade-up">
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                        Need Immediate Assistance?
                    </h2>
                    <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
                        Our support team is available to help you. Reach out through any of these channels:
                    </p>
                    <div className="flex flex-col sm:flex-row gap-6 justify-center items-center max-w-4xl mx-auto">
                        <a 
                            href="tel:7654108215"
                            className="flex items-center gap-3 bg-white text-blue-600 px-8 py-4 rounded-full font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                        >
                            <FaPhone className="text-2xl" />
                            <span>Call: 9934001660</span>
                        </a>
                        <a 
                            href="mailto:admin@educationaleternity.com"
                            className="flex items-center gap-3 bg-white text-blue-600 px-8 py-4 rounded-full font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300"
                        >
                            <FaEnvelope className="text-2xl" />
                            <span>Email Us</span>
                        </a>
                    </div>
                    <div className="mt-8 text-blue-100">
                        <div className="flex items-center justify-center gap-2">
                            <FaClock className="text-xl" />
                            <span className="text-lg">Support Hours: Monday - Friday, 9:00 AM - 6:00 PM IST</span>
                        </div>
                    </div>
                </div>
            </section>
        </Layout>
    );
};

export default Contact;
